import type React from 'react';
import {
  type ButtonHTMLAttributes,
  type FC,
  type HTMLAttributes,
  type PropsWithChildren,
  useRef,
} from 'react';

import { ScButton, type ScButtonProps, ScLink } from './styled';

interface ButtonProps extends HTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  target?: string;
  href?: string;
  to?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const handleLinkWrapping = (
  Component: React.ComponentType<any>,
  props: ButtonProps
) => {
  const { href, to, target, children, disabled, onLinkClick, ...rest } = props;

  const button = (
    <Component disabled={disabled || rest.isLoading} {...rest}>
      <span className="text">{children}</span>
    </Component>
  );

  if (href)
    return (
      <a
        className="button-link"
        href={href}
        rel={target ? undefined : 'noopener noreferrer'}
        target={target || '_blank'}
      >
        {button}
      </a>
    );

  if (to)
    return (
      <ScLink href={to} onClick={onLinkClick}>
        {button}
      </ScLink>
    );

  return button;
};

const ButtonComp: FC<PropsWithChildren<ScButtonProps>> = ({
  children,
  ...props
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <ScButton ref={ref} type="button" {...props}>
      {children}
      <span className="awareWrap">
        <div className="aware" />
      </span>
    </ScButton>
  );
};

export const Button: FC<
  ButtonProps &
    ScButtonProps & {
      component?: React.ComponentType<ButtonProps>;
    } & ButtonHTMLAttributes<HTMLButtonElement>
> = ({ component, ...rest }) =>
  handleLinkWrapping(component || ButtonComp, rest);
